import {
  Component,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StripeService } from '../../../../core/services/stripe/stripe.service';
import { UserService } from '../../../../core/services/user.service';
import { StripeMessagingConstants } from '../../../../core/constants/messaging.constants';
import { StripeFormMode } from '../../../../core/enums/base.enums';
import { PaymentMethodType } from '../../../../core/enums/data.enums';
import { CustomValidators } from '../../../../core/validators/custom-validators';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { FormValidatorMessages } from '../../../../core/enums/forms.enums';
import { UserSessionService } from '../../../../core/services/user-session.service';

export interface BankAccountPaymentMethodData {
  us_bank_account: { routing_number: string; account_number: string; account_holder_type: string };
  billing_details: { name: string; email?: string };
}

@Component({
  selector: 'ms-stripe-add-bank-account',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, InputLabel],
  templateUrl: './stripe-add-bank-account.component.html'
})
export class StripeAddBankAccountComponent {
  private stripeService = inject(StripeService);
  private userService = inject(UserService);
  private userSessionService = inject(UserSessionService);
  private fb = inject(FormBuilder);
  public FormValidatorMessages = FormValidatorMessages;
  public StripeMessagingConstants = StripeMessagingConstants;

  public userId = input.required<number>();
  public mode = input<StripeFormMode>(StripeFormMode.Setup);
  public paymentClientSecret = input<string | undefined>(undefined);

  public paymentMethodSaved = output<void>();
  public paymentConfirmed = output<void>();
  public bankDetailsCollected = output<BankAccountPaymentMethodData>();
  public cancelled = output<void>();

  public isSubmitting = signal(false);
  public errorMessage = signal<string | null>(null);

  public bankAccountForm: FormGroup = this.fb.group({
    accountHolderName: [this.userSessionService.activeUser()?.firstName + ' ' + this.userSessionService.activeUser()?.lastName || '', [Validators.required, Validators.minLength(2)]],
    email: [this.userSessionService.activeUser()?.emailAddress || '', [CustomValidators.email]],
    routingNumber: ['', [Validators.required, Validators.pattern(/^\d{9}$/)]],
    accountNumber: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(17), Validators.pattern(/^\d+$/)]],
    accountHolderType: ['individual']
  });

  public async onSubmit(): Promise<void> {
    if (this.bankAccountForm.invalid || this.isSubmitting()) {
      this.bankAccountForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    try {
      if (this.mode() === StripeFormMode.Collect) {
        this.bankDetailsCollected.emit(this.buildPaymentMethodData());
        return;
      } else if (this.mode() === StripeFormMode.Payment) {
        await this.confirmPayment();
      } else {
        await this.savePaymentMethod();
      }
    } catch {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_ADDING_BANK_ACCOUNT());
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async savePaymentMethod(): Promise<void> {
    const setupIntentResult = await this.userService.createSetupIntent(this.userId(), PaymentMethodType.AchDebit);

    if (!setupIntentResult.success || !setupIntentResult.data?.clientSecret) {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_CREATING_SETUP_INTENT());
      return;
    }

    const stripe = await this.stripeService.getStripeInstance();
    if (!stripe) {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_ADDING_BANK_ACCOUNT());
      return;
    }

    const confirmResult = await stripe.confirmUsBankAccountSetup(
      setupIntentResult.data.clientSecret,
      { payment_method: this.buildPaymentMethodData() }
    );

    if (confirmResult.error) {
      this.errorMessage.set(confirmResult.error.message || StripeMessagingConstants.STRIPE_ERROR_ADDING_BANK_ACCOUNT());
      return;
    }

    this.paymentMethodSaved.emit();
  }

  private async confirmPayment(): Promise<void> {
    const clientSecret = this.paymentClientSecret();
    if (!clientSecret) {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_ADDING_BANK_ACCOUNT());
      return;
    }

    const stripe = await this.stripeService.getStripeInstance();
    if (!stripe) {
      this.errorMessage.set(StripeMessagingConstants.STRIPE_ERROR_ADDING_BANK_ACCOUNT());
      return;
    }

    const confirmResult = await stripe.confirmUsBankAccountPayment(
      clientSecret,
      { payment_method: this.buildPaymentMethodData() }
    );

    if (confirmResult.error) {
      this.errorMessage.set(confirmResult.error.message || StripeMessagingConstants.STRIPE_ERROR_ADDING_BANK_ACCOUNT());
      return;
    }

    this.paymentConfirmed.emit();
  }

  private buildPaymentMethodData(): BankAccountPaymentMethodData {
    const { accountHolderName, email, routingNumber, accountNumber, accountHolderType } = this.bankAccountForm.value;
    const billingDetails: { name: string; email?: string } = { name: accountHolderName };
    if (email?.trim()) {
      billingDetails.email = email.trim();
    }

    return {
      us_bank_account: {
        routing_number: routingNumber,
        account_number: accountNumber,
        account_holder_type: accountHolderType,
      },
      billing_details: billingDetails
    };
  }

  public onCancel(): void {
    this.cancelled.emit();
  }
}
